import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteService } from "@/features/services/hooks/use-services";
import type { Service } from "@/features/services/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  CopyIcon,
  DotsThreeOutlineIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { ServiceDialog } from "./service-dialog";

export const Action = ({ service }: { service: Service }) => {
  const { t } = useTranslation("services");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const deleteService = useDeleteService();

  const handleDelete = async () => {
    try {
      await deleteService.mutateAsync(service.id);
      toast.success(t("messages.deleteSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  return (
    <>
      <div className="text-right flex items-center justify-end gap-2">
        <Button variant="ghost" size="icon" onClick={() => setIsEditOpen(true)}>
          <PencilSimpleIcon className="h-4 w-4" weight="fill" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{t("common.openMenu")}</span>
              <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsDuplicateOpen(true)}>
              <CopyIcon className="h-4 w-4" />
              {t("actions.duplicate")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="p-0">
              <Button
                variant="ghost"
                onClick={handleDelete}
                className="w-full justify-start text-destructive hover:text-destructive"
                confirm={t("messages.deleteConfirm", { name: service.name })}
              >
                <TrashIcon className="h-4 w-4" />
                {t("actions.delete")}
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ServiceDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        service={service}
      />
      <ServiceDialog
        open={isDuplicateOpen}
        onOpenChange={setIsDuplicateOpen}
        service={{ ...service, id: "" }}
      />
    </>
  );
};
